import { useEffect, useState, useRef } from 'react';
import { ExpandableTable, LtToggle, LtIcon, LtButton, IndicatorModal, SearchComponent } from 'limetray-ui-kit';
import { userManagerConstants } from '../../../constants/userManagerConstants';
import { useToast } from '../../../context/ToastContext';
import LtLoader from '../../../components/LtLoader/index';
import CreateEditUserModal from './CreateEditUserModal';

import '../index.scss';

function UserListing({
	getAllUsers,
	allUsersLoading,
	allUsersError,
	allUsersResponse,
	updateUserStatus,
	deleteUser,
	// Create user
	createUser,
	createUserLoading,
	createUserFeedback,
	clearCreateUserFeedback,
	// Edit user
	editUser,
	editUserLoading,
	editUserFeedback,
	clearEditUserFeedback,
	// Get user (edit pre-population)
	getUser,
	getUserLoading,
	getUserData,
	getUserError,
	clearGetUser,
}) {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [searchQuery, setSearchQuery] = useState('');
	const [pendingAction, setPendingAction] = useState(null);
	const [renderSearch, setRenderSearch] = useState(false);
	const { showToast } = useToast();

	// Create modal state
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

	// Edit modal state — holds the full user row object clicked from the table
	const [editUserRow, setEditUserRow] = useState(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const searchWrapperRef = useRef(null);

	// Fetch users whenever current page, page size or search query changes
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			getAllUsers({
				offset: (currentPage - 1) * pageSize,
				size: pageSize,
				searchParam: searchQuery
			});
		}, 300); // 300ms debounce

		return () => clearTimeout(timeoutId);
	}, [getAllUsers, currentPage, pageSize, searchQuery]);

	const handleSearchIconClick = () => {
		setRenderSearch(true);
		setTimeout(() => {
			const input = searchWrapperRef.current?.querySelector('input');
			input?.focus();
		}, 0);
	};

	const handleConfirm = () => {
		if (!pendingAction) return;

		const isDelete = pendingAction.type === 'delete';
		const successTitle = isDelete ? userManagerConstants.userListingMessages.deleteSuccessTitle : userManagerConstants.userListingMessages.statusSuccessTitle;
		const successDesc = isDelete ? userManagerConstants.userListingMessages.deleteSuccessDesc : userManagerConstants.userListingMessages.statusSuccessDesc;
		const errorTitle = isDelete ? userManagerConstants.userListingMessages.deleteErrorTitle : userManagerConstants.userListingMessages.statusErrorTitle;

		const basePayload = {
			offset: (currentPage - 1) * pageSize,
			size: pageSize,
			searchParam: searchQuery,
			onSuccess: () => showToast({ variant: 'success', title: successTitle, description: successDesc }),
			onError: (msg) => showToast({ variant: 'error', title: errorTitle, description: msg || userManagerConstants.userListingMessages.defaultErrorDesc })
		};

		if (pendingAction.type === 'status') {
			updateUserStatus({
				brandUserId: pendingAction.userId,
				currentSuspended: pendingAction.value,
				...basePayload
			});
		} else if (pendingAction.type === 'delete') {
			deleteUser({
				brandUserId: pendingAction.userId,
				...basePayload
			});
		}
		setPendingAction(null);
	};

	const handleEditClick = (user) => {
		setEditUserRow(user);
		setIsEditModalOpen(true);
	};

	const handleCreateModalClose = () => {
		setIsCreateModalOpen(false);
	};

	const handleEditModalClose = () => {
		setIsEditModalOpen(false);
		setEditUserRow(null);
		clearGetUser?.();
	};

	const usersList = allUsersResponse?.result?.brandUsers || [];
	const totalUsers = allUsersResponse?.result?.userCount || 0;
	const totalPage = Math.ceil(totalUsers / pageSize) || 1;

	// ExpandableTable Columns Configuration
	const columns = [
		{ header: userManagerConstants.userListingTableHeaders.name, accessor: (user) => user.fullName || '', width: '25%' },
		{ header: userManagerConstants.userListingTableHeaders.email, accessor: (user) => user.primaryEmail || '', width: '25%' },
		{ header: userManagerConstants.userListingTableHeaders.role, accessor: (user) => user.roleName || '', width: '20%' },
		{
			header: userManagerConstants.userListingTableHeaders.status,
			width: '10%',
			accessor: (user) => (
				<div className="status-toggle-wrapper">
					<LtToggle
						checked={!user.suspended}
						onChange={() => {
							setPendingAction({
								type: 'status',
								userId: user.brandUserId,
								value: user.suspended,
								header: userManagerConstants.userListingMessages.statusConfirmHeader,
								content: userManagerConstants.userListingMessages.statusConfirmContent(user.fullName),
							});
						}}
					/>
				</div>
			),
		},
		{
			header: userManagerConstants.userListingTableHeaders.actions,
			width: '20%',
			accessor: (user) => (
				<div className="action-buttons-wrapper">
					<LtButton
						transparent
						customStyles={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							padding: '6px'
						}}
						leftElement={
							<LtIcon
								useReactIcon
								reactIcon={userManagerConstants.icons.editIcon}
								size='tiny'
							/>
						}
						onClick={() => handleEditClick(user)}
					/>
					<LtButton
						transparent
						customStyles={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							padding: '6px'
						}}
						leftElement={
							<LtIcon
								useReactIcon
								reactIcon={userManagerConstants.icons.trashIcon}
								size='tiny'
							/>
						}
						onClick={() => {
							setPendingAction({
								type: 'delete',
								userId: user.brandUserId,
								header: userManagerConstants.userListingMessages.deleteConfirmHeader,
								content: userManagerConstants.userListingMessages.deleteConfirmContent(user.fullName),
							});
						}}
					/>
				</div>
			),
		},
	];

	const currentOffset = (currentPage - 1) * pageSize;

	return (
		<div className="user-list-container">
			<div className="list-header-section">
				<div>
					<h1 className="list-title">{userManagerConstants.userListingMessages.title}</h1>
				</div>
				<div className="header-actions">
					{/* TODO: Implement download users list functionality */}
					{/* <LtButton
						type="button"
						customStyles={{
							backgroundColor: '#ffffff',
							borderRadius: '50%',
							padding: '6px',
							width: '38px',
							height: '38px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
						onClick={() => { }}
						aria-label="Download"
						leftElement={
							<LtIcon
								useReactIcon
								reactIcon={userManagerConstants.icons.cloudDownloadIcon}
								size="small"
							/>
						}
					/> */}

					<div className="searchWrapper" ref={searchWrapperRef}>
						<SearchComponent
							placeholder={userManagerConstants.userListingMessages.searchPlaceholder}
							onChange={(valOrEvent) => {
								const val = valOrEvent?.target ? valOrEvent.target.value : valOrEvent;
								setSearchQuery(val);
								setCurrentPage(1); // Reset to page 1 on search
							}}
							value={searchQuery}
							onBlur={() => {
								if (!searchQuery) {
									setRenderSearch(false);
								}
							}}
							className={`searchInput ${(renderSearch || searchQuery) ? 'searchInputExpanded' : ''}`}
						/>
						<LtButton
							type="button"
							className={`${(renderSearch || searchQuery) ? 'searchButtonHidden' : ''}`}
							customStyles={{
								backgroundColor: '#ffffff',
								borderRadius: '50%',
								padding: '6px',
								width: '38px',
								height: '38px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}
							onClick={handleSearchIconClick}
							aria-label="Search"
							leftElement={
								<LtIcon
									useReactIcon
									reactIcon={userManagerConstants.icons.searchIcon}
									size="small"
								/>
							}
						/>
					</div>

					<LtButton
						label={userManagerConstants.userListingMessages.createUserButton}
						customStyles={{
							backgroundColor: '#00cd7c',
							color: '#ffffff',
							border: 'none',
							borderRadius: '24px',
							fontSize: '13px',
							padding: '12px 18px',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
						leftElement={
							<LtIcon
								useReactIcon
								reactIcon={userManagerConstants.icons.plusIcon}
								size="mini"
							/>
						}
						onClick={() => setIsCreateModalOpen(true)}
					/>
				</div>
			</div>

			{allUsersError ? (
				<div className="statusState">
					<p className="errorText">{userManagerConstants.userListingMessages.fetchErrorDesc}</p>
				</div>
			) : (allUsersLoading || !allUsersResponse) && usersList.length === 0 ? (
				<div className="statusState">
					<LtLoader size="medium" inline="centered" message={userManagerConstants.userListingMessages.loadingMessage} />
				</div>
			) : (
				<div className="table-wrapper">
					<ExpandableTable
						tableData={usersList}
						columns={columns}
						expandable={false}
						isLoading={allUsersLoading}
						paginationEnabled={true}
						paginationConfig={{
							mode: 'server',
							currentPage: currentPage,
							totalPages: totalPage,
							totalItems: totalUsers,
							pageSize: pageSize,
							pageSizeOptions: [10, 20, 30, 50],
							showPageNumbers: true,
							maxPageButtons: 5,
							onPageChange: (page) => setCurrentPage(page),
							onPageSizeChange: (size) => {
								setPageSize(size);
								setCurrentPage(1);
							},
						}}
						tableHeaderStyles={{
							background: 'white',
						}}
						tableHeaderCellStyles={{
							color: "#6b7280",
							fontSize: "12px",
							fontWeight: 500
						}}
						tableBodyCellStyles={{
							padding: "12px 16px"
						}}
						emptyMessage={userManagerConstants.userListingMessages.emptyMessage}
					/>
				</div>
			)}

			<IndicatorModal
				open={pendingAction !== null}
				title={pendingAction?.header || ''}
				message={pendingAction?.content || ''}
				variant="warning"
				onClose={() => setPendingAction(null)}
				actions={[
					{
						label: userManagerConstants.userListingMessages.cancelButton,
						onClick: () => setPendingAction(null),
						backgroundColor: '#ffffff',
						color: '#374151',
						borderColor: '#efefef',
					},
					{
						label: userManagerConstants.userListingMessages.confirmButton,
						onClick: handleConfirm,
						backgroundColor: '#f59e0b',
						color: '#ffffff',
					},
				]}
				actionsAlignment="center"
				maxWidth="420px"
			/>

			{/* Create User Modal */}
			<CreateEditUserModal
				open={isCreateModalOpen}
				onClose={handleCreateModalClose}
				mode="create"
				createUser={createUser}
				createUserLoading={createUserLoading}
				createUserFeedback={createUserFeedback}
				clearCreateUserFeedback={clearCreateUserFeedback}
				offset={currentOffset}
				pageSize={pageSize}
				searchQuery={searchQuery}
			/>

			{/* Edit User Modal */}
			<CreateEditUserModal
				open={isEditModalOpen}
				onClose={handleEditModalClose}
				mode="edit"
				editUserRow={editUserRow}
				editUser={editUser}
				editUserLoading={editUserLoading}
				editUserFeedback={editUserFeedback}
				clearEditUserFeedback={clearEditUserFeedback}
				getUser={getUser}
				getUserLoading={getUserLoading}
				getUserData={getUserData}
				getUserError={getUserError}
				clearGetUser={clearGetUser}
				offset={currentOffset}
				pageSize={pageSize}
				searchQuery={searchQuery}
			/>
		</div>
	);
}

export default UserListing;
